import React, { useState } from 'react';
import { Farm, Pod, ArtificialReef } from '../types';
import { apiService } from '../apiService';
import './FarmSidebar.css';

type TabType = 'farms' | 'reefs' | 'predictions';

interface SidebarProps {
  farms: Farm[];
  pods: Pod[];
  reefs: ArtificialReef[];
  onFarmUpdate: () => void;
  onFarmDeleted: (farmId: number) => void;
  onFarmSelect: (farm: Farm | null) => void;
  onFarmZoom: (farm: Farm) => void;
  onReefDeleted: (reefId: number) => void;
  onReefSelect: (reef: ArtificialReef | null) => void;
  onReefZoom: (reef: ArtificialReef) => void;
  selectedFarm: Farm | null;
  selectedReef: ArtificialReef | null;
  predictionHistory?: Array<{ 
    id: number; 
    timestamp: Date; 
    abundance: number; 
    richness: number; 
    numReefs: number;
    bounds: number[][];
  }>;
  onPredictionClick?: (bounds: number[][]) => void;
}

interface EditFarmModalProps {
  farm: Farm;
  onClose: () => void;
  onUpdate: () => void;
}

const EditFarmModal: React.FC<EditFarmModalProps> = ({ farm, onClose, onUpdate }) => {
  const [name, setName] = useState(farm.name);
  const [podCount, setPodCount] = useState(5);
  const [scaleFactor, setScaleFactor] = useState(1.0);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize pod count based on current pods
  React.useEffect(() => {
    const fetchPodCount = async () => {
      try {
        const currentPods = await apiService.getFarmPods(farm.id);
        setPodCount(currentPods.length);
      } catch (error) {
        console.error('Error fetching pod count:', error);
      }
    };
    fetchPodCount();
  }, [farm.id]);

  const calculatePolygonCenter = (coordinates: number[][]) => {
    const centerLng = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length;
    const centerLat = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length;
    return [centerLng, centerLat];
  };

  const scalePolygon = (coordinates: number[][], center: number[], factor: number) => {
    return coordinates.map(coord => [
      center[0] + (coord[0] - center[0]) * factor,
      center[1] + (coord[1] - center[1]) * factor
    ]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Update farm geometry if scale factor is different from 1
      let updatedGeometry = farm.geometry;
      if (scaleFactor !== 1.0) {
        const coordinates = farm.geometry.coordinates[0];
        const center = calculatePolygonCenter(coordinates);
        const scaledCoordinates = scalePolygon(coordinates, center, scaleFactor);
        
        updatedGeometry = {
          type: 'Polygon',
          coordinates: [scaledCoordinates]
        };
      }

      // Update farm name and geometry
      await apiService.updateFarm(farm.id, { 
        name,
        geometry: updatedGeometry
      });

      // Get current pods for this farm
      const currentPods = await apiService.getFarmPods(farm.id);
      
      // Add or remove pods to match desired count
      if (podCount > currentPods.length) {
        // Add pods
        const podsToAdd = podCount - currentPods.length;
        const farmCoords = updatedGeometry.coordinates[0];
        const centerLng = farmCoords.reduce((sum, coord) => sum + coord[0], 0) / farmCoords.length;
        const centerLat = farmCoords.reduce((sum, coord) => sum + coord[1], 0) / farmCoords.length;

        for (let i = 0; i < podsToAdd; i++) {
          const offsetLng = (Math.random() - 0.5) * 0.01;
          const offsetLat = (Math.random() - 0.5) * 0.01;
          
          await apiService.createPod({
            farm_id: farm.id,
            name: `Pod ${currentPods.length + i + 1}`,
            position: {
              type: 'Point',
              coordinates: [centerLng + offsetLng, centerLat + offsetLat]
            },
            size: Math.random() * 1.5 + 0.5
          });
        }
      } else if (podCount < currentPods.length) {
        // Remove excess pods
        const podsToRemove = currentPods.slice(podCount);
        for (const pod of podsToRemove) {
          await apiService.deletePod(pod.id);
        }
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating farm:', error);
      alert('Failed to update farm');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Farm: {farm.name}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="farmName">Farm Name:</label>
            <input
              id="farmName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="podCount">Number of Pods:</label>
            <input
              id="podCount"
              type="number"
              min="0"
              max="50"
              value={podCount}
              onChange={(e) => setPodCount(parseInt(e.target.value))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="scaleFactor">Polygon Size (Scale Factor):</label>
            <input
              id="scaleFactor"
              type="number"
              min="0.1"
              max="5.0"
              step="0.1"
              value={scaleFactor}
              onChange={(e) => setScaleFactor(parseFloat(e.target.value))}
              required
            />
            <small style={{color: '#666', fontSize: '0.8em', display: 'block'}}>
              1.0 = current size, 2.0 = double size, 0.5 = half size
            </small>
          </div>
          <div className="form-actions">
            <button type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Farm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const FarmSidebar: React.FC<SidebarProps> = ({ 
  farms, 
  pods, 
  reefs,
  onFarmUpdate, 
  onFarmDeleted,
  onFarmSelect, 
  onFarmZoom,
  onReefDeleted,
  onReefSelect,
  onReefZoom,
  selectedFarm,
  selectedReef,
  predictionHistory = [],
  onPredictionClick
}) => {
  const [editingFarm, setEditingFarm] = useState<Farm | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('farms');

  const handleDeleteFarm = async (farm: Farm) => {
    try {
      console.log('Deleting farm with ID:', farm.id);
      await apiService.deleteFarm(farm.id);
      console.log('Farm deleted successfully');
      onFarmDeleted(farm.id);
      if (selectedFarm?.id === farm.id) {
        onFarmSelect(null);
      }
    } catch (error) {
      console.error('Error deleting farm:', error);
      alert(`Failed to delete farm: ${error}`);
    }
  };

  const handleDeleteReef = async (reef: ArtificialReef) => {
    try {
      console.log('Deleting reef with ID:', reef.id);
      await apiService.deleteReef(reef.id);
      console.log('Reef deleted successfully');
      onReefDeleted(reef.id);
      if (selectedReef?.id === reef.id) {
        onReefSelect(null);
      }
    } catch (error) {
      console.error('Error deleting reef:', error);
      alert(`Failed to delete reef: ${error}`);
    }
  };

  const getFarmPodCount = (farmId: number) => {
    return pods.filter(pod => pod.farm_id === farmId).length;
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2>Management</h2>
        <div className="tab-buttons">
          <button 
            className={`tab-button ${activeTab === 'farms' ? 'active' : ''}`}
            onClick={() => setActiveTab('farms')}
          >
            Farms ({farms.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'reefs' ? 'active' : ''}`}
            onClick={() => setActiveTab('reefs')}
          >
            Reefs ({reefs.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'predictions' ? 'active' : ''}`}
            onClick={() => setActiveTab('predictions')}
          >
            Predictions ({predictionHistory.length})
          </button>
        </div>
      </div>

      {activeTab === 'farms' && (
        <div className="farms-tab">
          <div className="farm-list">
            {farms.length === 0 ? (
              <p className="no-farms">No farms created yet</p>
            ) : (
              farms.map(farm => (
                <div 
                  key={farm.id} 
                  className={`farm-item ${selectedFarm?.id === farm.id ? 'selected' : ''}`}
                  onClick={() => {
                    onFarmSelect(farm);
                    onFarmZoom(farm);
                  }}
                >
                  <div className="farm-info">
                    <h4>{farm.name}</h4>
                    <p>Pods: {getFarmPodCount(farm.id)}</p>
                    <p className="farm-date">
                      Created: {new Date(farm.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="farm-actions">
                    <button 
                      className="edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingFarm(farm);
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFarm(farm);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'reefs' && (
        <div className="reefs-tab">
          <div className="reef-list">
            {reefs.length === 0 ? (
              <p className="no-reefs">No reefs created yet</p>
            ) : (
              reefs.map(reef => (
                <div 
                  key={reef.id} 
                  className={`reef-item ${selectedReef?.id === reef.id ? 'selected' : ''}`}
                  onClick={() => {
                    onReefSelect(reef);
                    onReefZoom(reef);
                  }}
                >
                  <div className="reef-info">
                    <h4>{reef.name}</h4>
                    <p>Detection Range: {reef.detection_range}m</p>
                    <p>Fish Species: {reef.fish_species_count}</p>
                    <p className="reef-date">
                      Created: {new Date(reef.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="reef-actions">
                    <button 
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReef(reef);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'predictions' && (
        <div className="predictions-tab">
          <div className="prediction-list">
            {predictionHistory.length === 0 ? (
              <p className="no-predictions">No predictions yet. Use "Predict Biodiversity in Area" to create predictions.</p>
            ) : (
              predictionHistory.map(prediction => (
                <div 
                  key={prediction.id} 
                  className="prediction-item"
                  onClick={() => onPredictionClick && onPredictionClick(prediction.bounds)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="prediction-header">
                    <h4>Area Prediction</h4>
                    <span className="prediction-time">
                      {prediction.timestamp.toLocaleString()}
                    </span>
                  </div>
                  <div className="prediction-stats">
                    <div className="stat-box">
                      <label>Reefs</label>
                      <span className="stat-value">{prediction.numReefs}</span>
                    </div>
                    <div className="stat-box abundance">
                      <label>Abundance</label>
                      <span className="stat-value">{prediction.abundance.toFixed(2)}</span>
                    </div>
                    <div className="stat-box richness">
                      <label>Richness</label>
                      <span className="stat-value">{prediction.richness.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {editingFarm && (
        <EditFarmModal
          farm={editingFarm}
          onClose={() => setEditingFarm(null)}
          onUpdate={onFarmUpdate}
        />
      )}
    </div>
  );
};
